import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Search, Loader2, ChevronLeft, ChevronRight, BookOpen, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import MySheetsTab from './MySheetsTab';
import InformationForm from './InformationForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` });

const LEVELS = ['CP', 'ADM', 'DM'];
const PAGE = 25;

const AdminWorkbooks = () => {
  const { user } = useOutletContext();
  const [level, setLevel] = useState('CP');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refreshProductionData = async () => {
    if (!window.confirm('Regenerate the production data snapshot from the current data?\n\nAfter this completes, click Deploy to push everything to your live site.')) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API}/crm/admin/regenerate-seed`, { method: 'POST', headers: getH() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Snapshot ready · ${data.documents.toLocaleString()} records (${data.sizeMB} MB). Now click Deploy to sync to your live site.`, { duration: 8000 });
    } catch (e) {
      toast.error(`Refresh failed: ${e.message}`);
    }
    setSyncing(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ level, skip: String(skip), limit: String(PAGE) });
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`${API}/hierarchy-accounts/list?${params}`, { headers: getH() });
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { setItems([]); setTotal(0); }
    setLoading(false);
  }, [level, skip, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [level, search]);

  return (
    <div className="p-4 lg:p-6" data-testid="admin-workbooks">
      <div className="rounded-2xl mb-5 px-6 py-5 flex items-center gap-4 shadow-lg"
        style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 50%,#1f3864 100%)' }}>
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-amber-300" />
        </div>
        <div>
          <h1 className="text-white font-extrabold text-xl tracking-tight">DM / ADM / CP Workbooks</h1>
          <p className="text-blue-200 text-xs mt-1">Open any Channel Partner, ADM or DM's Index &amp; Top 10 sheets to view and edit their data.</p>
        </div>
        <button onClick={refreshProductionData} disabled={syncing}
          data-testid="refresh-production-data-btn"
          className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-stone-900 font-bold text-sm shadow transition-colors">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? 'Preparing…' : 'Refresh Production Data'}
        </button>
      </div>
      <p className="text-[11px] text-stone-400 -mt-3 mb-4 px-1">
        “Refresh Production Data” snapshots everything you see here so a single <b>Deploy</b> pushes it live — no manual re-entry.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* Picker */}
        <div className="bg-white border border-stone-200 rounded-xl p-4 h-fit">
          <div className="flex gap-1.5 mb-3">
            {LEVELS.map(l => (
              <button key={l} onClick={() => setLevel(l)}
                data-testid={`wb-level-${l}`}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${level === l ? 'bg-[#1F3864] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${level} by ID / name / mobile…`}
              data-testid="wb-search-input"
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          <div className="text-xs text-stone-400 mb-2">{total.toLocaleString()} {level}s</div>
          <div className="space-y-1 max-h-[55vh] overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-stone-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="text-center text-stone-400 py-8 text-sm">No results</div>
            ) : items.map(it => (
              <button key={it.id} onClick={() => setSelected(it)}
                data-testid={`wb-pick-${it.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selected?.id === it.id ? 'bg-blue-50 border border-blue-300' : 'hover:bg-stone-50 border border-transparent'}`}>
                <div className="text-sm font-medium text-stone-800 truncate">{it.name || it.id}</div>
                <div className="text-xs text-stone-400">{it.id}{it.mobile ? ` · ${it.mobile}` : ''}</div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {total > PAGE && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
                className="flex items-center gap-1 text-xs text-stone-600 disabled:opacity-30" data-testid="wb-prev">
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-xs text-stone-400">{skip + 1}–{Math.min(skip + PAGE, total)}</span>
              <button disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}
                className="flex items-center gap-1 text-xs text-stone-600 disabled:opacity-30" data-testid="wb-next">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Workbook */}
        <div className="bg-white border border-stone-200 rounded-xl p-4 min-h-[400px]">
          {selected ? (
            <MySheetsTab key={selected.id} user={user} empId={selected.id} ownerName={selected.name} overviewContent={<InformationForm empId={selected.id} />} />
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-stone-400">
              <BookOpen className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">Select a {level} on the left to open their workbook.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminWorkbooks;
